'use es6';

import { useState, useEffect } from 'react';
import EmailScheduling from './EmailScheduling';
import EmailAssetDialogFooter from './EmailAssetDialogFooter';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H2 } from 'foundations-components/transitional/components/H2';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Form } from 'foundations-components/transitional/components/Form';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { getEventValue } from '../../../utils/jsEventUtils';
import { SCHEDULE_FREQUENCY, DEFAULT_SCHEDULED_HOUR, getFormattedDayLabels, getFormattedTimeLabels } from '../../../lib/schedulingLogic';
import EmailRecipientSelect from './EmailRecipientSelect';
import EmailExternalRecipientSelect from './EmailExternalRecipientSelect';
import { Set as ImmutableSet } from 'immutable';
import useUserInfo from '../../../access/useUserInfo';
import { getUserId } from '../../../data/schemas/userInfoSchema';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import { DASHBOARD_OBJECT_TYPE } from 'dashboard-lib/public/emails/emails';
import { DEFAULT_EXPORT_TYPE, isUnsupportedDataExportType } from '../../shared/emailLogic';
import { useHasReportingAdminAccess, useHasEmailExternalRecipientsAccess } from 'reporting-action-components/access/scopes/hooks/useHasScope';
import { noop } from '../../shared/noop';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RECURRING_EMAIL_OPTION = 'recurring';
const SEND_NOW_EMAIL_OPTION = 'now';
const EmailCadenceOptions = ({
  onChange
}) => {
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.emailReport.dialog.form.emailCadenceOptions.optionGroupLabel"
    }),
    children: /*#__PURE__*/_jsxs(ToggleGroup, {
      name: "emailType",
      children: [/*#__PURE__*/_jsx(RadioInput, {
        defaultChecked: true,
        value: SEND_NOW_EMAIL_OPTION,
        onChange: e => onChange(getEventValue(e)),
        "data-test-id": "email-report-send-now",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.emailReport.dialog.form.emailCadenceOptions.singleEmail"
        })
      }), /*#__PURE__*/_jsx(RadioInput, {
        value: RECURRING_EMAIL_OPTION,
        onChange: e => onChange(getEventValue(e)),
        "data-test-id": "email-report-schedule-email",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.emailReport.dialog.form.emailCadenceOptions.recurringEmail"
        })
      })]
    })
  });
};
const ScheduledEmailNicknameTextInput = ({
  scheduledEmailNickname,
  onChange
}) => {
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.emailReport.dialog.form.emailFormSection.emailNicknameLabel"
    }),
    help: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.emailReport.dialog.form.emailFormSection.emailNicknameMessage"
    }),
    required: true,
    children: /*#__PURE__*/_jsx(TextInput, {
      autoFocus: true,
      defaultValue: scheduledEmailNickname,
      onChange: e => onChange(getEventValue(e)),
      "data-test-id": "email-report-schedule-nickname"
    })
  });
};
const EmailSubjectTextInput = ({
  onChange,
  emailSubject
}) => {
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.emailReport.dialog.form.emailFormSection.emailSubjectLabel"
    }),
    children: /*#__PURE__*/_jsx(TextInput, {
      defaultValue: emailSubject,
      onChange: e => onChange(getEventValue(e)),
      "data-test-id": "email-report-subject-text-input"
    })
  });
};
const EmailMessageTextInput = ({
  onChange,
  emailMessage
}) => {
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.emailReport.dialog.form.emailFormSection.emailMessageLabel"
    }),
    children: /*#__PURE__*/_jsx(TextArea, {
      defaultValue: emailMessage,
      onChange: e => onChange(getEventValue(e))
    })
  });
};
const EmailScheduleSummary = ({
  scheduleFrequency,
  isUtc,
  scheduleOnDays,
  scheduleAtHour
}) => {
  if (scheduleFrequency === SCHEDULE_FREQUENCY.DAILY) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.emailReport.dialog.form.emailFormSection.scheduleSummary.dailyDescription",
      options: Object.assign({}, getFormattedTimeLabels({
        scheduleAtHour,
        isUtc
      }))
    });
  }
  if (scheduleOnDays.length) {
    if (scheduleFrequency === SCHEDULE_FREQUENCY.WEEKLY) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.emailReport.dialog.form.emailFormSection.scheduleSummary.weeklyDescription",
        options: Object.assign({
          days: I18n.formatList(getFormattedDayLabels({
            scheduleFrequency,
            isUtc,
            scheduleOnDays
          }))
        }, getFormattedTimeLabels({
          scheduleAtHour,
          isUtc
        }))
      });
    } else if (scheduleFrequency === SCHEDULE_FREQUENCY.MONTHLY) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.emailReport.dialog.form.emailFormSection.scheduleSummary.monthlyDescription",
        options: Object.assign({
          days: I18n.formatList(getFormattedDayLabels({
            scheduleFrequency,
            isUtc,
            scheduleOnDays
          }))
        }, getFormattedTimeLabels({
          scheduleAtHour,
          isUtc
        }))
      });
    }
  }
  return null;
};
const scheduledOnDaysPropDefault = [];
const recipientUserIdsPropDefault = [];
const assetSpecificPayloadPropDefault = {};
const assetSpecificTrackingPropsPropDefault = {};
const EmailAssetDialog = ({
  appUsageTracker,
  onSubmit,
  onClose,
  id: scheduledEmailId = null,
  isUtc = false,
  isEditingScheduledEmail = false,
  nickname: prevScheduledEmailNickname = '',
  ownerId = null,
  scheduleFrequency: prevScheduledFrequency = SCHEDULE_FREQUENCY.DAILY,
  scheduledOnDays: prevScheduledOnDays = scheduledOnDaysPropDefault,
  scheduledAtHour: prevScheduledAtHour = DEFAULT_SCHEDULED_HOUR,
  subject: prevEmailSubject = '',
  recipientUserIds: prevRecipientUserIds = recipientUserIdsPropDefault,
  message: prevEmailMessage = '',
  objectType,
  assetName = '',
  assetSpecificPayload = assetSpecificPayloadPropDefault,
  assetSpecificTrackingProps = assetSpecificTrackingPropsPropDefault,
  previewUrl,
  renderAdditionalFields = noop,
  validationError = false,
  resetSelectedReports = noop
}) => {
  const {
    data: userInfo
  } = useUserInfo();
  const currentUserId = getUserId(userInfo);
  const emailOwnerId = ownerId || currentUserId;
  const dataType = assetSpecificPayload.exportType;
  const [emailCadenceOption, setEmailCadenceOption] = useState(isEditingScheduledEmail ? RECURRING_EMAIL_OPTION : SEND_NOW_EMAIL_OPTION);
  const [scheduledEmailNickname, setScheduleEmailNickname] = useState(prevScheduledEmailNickname);
  const [scheduleFrequency, setScheduleFrequency] = useState(prevScheduledFrequency);
  const [scheduleOnDays, setScheduleOnDays] = useState(prevScheduledOnDays);
  const [scheduleAtHour, setScheduleAtHour] = useState(prevScheduledAtHour);
  const [recipientSet, setRecipientSet] = useState(ImmutableSet(prevRecipientUserIds));
  const [externalRecipientSet, setExternalRecipientSet] = useState(new Set());
  const [externalValidationError, setExternalValidationError] = useState(false);
  const [emailSubject, setEmailSubject] = useState(prevEmailSubject || assetName);
  const [emailMessage, setEmailMessage] = useState(prevEmailMessage);
  const shouldRenderEmailSchedulingComponents = emailCadenceOption === RECURRING_EMAIL_OPTION;
  const isAdmin = useHasReportingAdminAccess();
  const hasEmailExternalRecipientsAccess = useHasEmailExternalRecipientsAccess();
  const shouldRenderEmailExternalField = isAdmin && !shouldRenderEmailSchedulingComponents && hasEmailExternalRecipientsAccess && !isUnsupportedDataExportType(dataType);
  const handleSubmit = e => {
    e.preventDefault();
    const payload = shouldRenderEmailSchedulingComponents ? Object.assign({
      subject: emailSubject,
      message: emailMessage,
      nickname: scheduledEmailNickname,
      exportType: DEFAULT_EXPORT_TYPE.NONE,
      reportsPerPage: 1,
      recipientUserIds: recipientSet.toArray(),
      ownerId: emailOwnerId,
      scheduleFrequency,
      scheduledOnDays: scheduleOnDays,
      scheduledAtHour: scheduleAtHour,
      scheduledEmailId,
      isUtc
    }, assetSpecificPayload) : Object.assign({
      subject: emailSubject,
      message: emailMessage,
      exportType: DEFAULT_EXPORT_TYPE.NONE,
      reportsPerPage: 1,
      recipientUserIds: recipientSet.toArray(),
      externalRecipientEmails: Array.from(externalRecipientSet)
    }, assetSpecificPayload);
    onSubmit({
      isSchedulingEmail: shouldRenderEmailSchedulingComponents,
      payload
    });
  };

  // Disable the submit button if there are no recipients. If the user is trying
  // to schedule an email, then disable the submit button if there is no
  // email nickname, or if the user hasn't chosen days to send the email on
  // for weekly/monthly frequencies.

  const disableSubmit = () => {
    if (validationError) {
      return true;
    }
    if (externalValidationError) {
      return true;
    }
    if (recipientSet.isEmpty() && externalRecipientSet.size === 0) {
      return true;
    }
    if (shouldRenderEmailSchedulingComponents) {
      if (!scheduledEmailNickname) {
        return true;
      }
      if (scheduleFrequency !== SCHEDULE_FREQUENCY.DAILY && scheduleOnDays.length === 0) {
        return true;
      }
      if (recipientSet.isEmpty()) return true;
    }
    return false;
  };

  // When the userId is finally fetched and updated, update state for the component,
  // but only if there is not a previously set list of recipients.
  useEffect(() => {
    if (prevRecipientUserIds.length === 0) {
      setRecipientSet(ImmutableSet([currentUserId]));
    }
  }, [currentUserId, prevRecipientUserIds]);
  return /*#__PURE__*/_jsxs(ModalPanel, {
    width: 600,
    "data-test-id": "email-report-dialog",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(H2, {
        children: objectType === DASHBOARD_OBJECT_TYPE ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.emailReport.dialog.dashboardTitle"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.emailReport.dialog.reportTitle"
        })
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx(Form, {
        autoComplete: "off",
        onSubmit: handleSubmit,
        children: /*#__PURE__*/_jsxs(View, {
          paddingBlockEnd: "600",
          children: [!isEditingScheduledEmail && /*#__PURE__*/_jsx(EmailCadenceOptions, {
            onChange: cadenceOption => {
              // Since DBs are subject to change, we must reset the selected
              // reports whenever a user changes the cadence option
              // to ensure that an email does ont get scheduled with selected
              // emails.
              setEmailCadenceOption(cadenceOption);
              resetSelectedReports();
            }
          }), shouldRenderEmailSchedulingComponents && /*#__PURE__*/_jsx(ScheduledEmailNicknameTextInput, {
            scheduledEmailNickname: scheduledEmailNickname,
            onChange: setScheduleEmailNickname
          }), /*#__PURE__*/_jsx(EmailRecipientSelect, {
            onChange: setRecipientSet,
            value: recipientSet
          }), shouldRenderEmailExternalField && /*#__PURE__*/_jsx(EmailExternalRecipientSelect, {
            appUsageTracker: appUsageTracker,
            onChange: setExternalRecipientSet,
            externalRecipients: externalRecipientSet,
            onValidationError: setExternalValidationError,
            validationErrorState: externalValidationError
          }), /*#__PURE__*/_jsx(EmailSubjectTextInput, {
            onChange: setEmailSubject,
            emailSubject: emailSubject
          }), /*#__PURE__*/_jsx(EmailMessageTextInput, {
            onChange: setEmailMessage,
            emailMessage: emailMessage
          }), shouldRenderEmailSchedulingComponents && /*#__PURE__*/_jsx(EmailScheduling, {
            scheduleFrequency: scheduleFrequency,
            setScheduleFrequency: setScheduleFrequency,
            scheduleOnDays: scheduleOnDays,
            setScheduleOnDays: setScheduleOnDays,
            scheduleAtHour: scheduleAtHour,
            setScheduleAtHour: setScheduleAtHour
          }), renderAdditionalFields(shouldRenderEmailSchedulingComponents), shouldRenderEmailSchedulingComponents && /*#__PURE__*/_jsx(FormControl, {
            children: /*#__PURE__*/_jsx(EmailScheduleSummary, {
              scheduleFrequency: scheduleFrequency,
              scheduleOnDays: scheduleOnDays,
              scheduleAtHour: scheduleAtHour,
              isUtc: isUtc
            })
          })]
        })
      })
    }), /*#__PURE__*/_jsx(DialogFooter, {
      children: /*#__PURE__*/_jsx(EmailAssetDialogFooter, {
        appUsageTracker: appUsageTracker,
        shouldRenderEmailSchedulingComponents: shouldRenderEmailSchedulingComponents,
        shouldRenderExternalEmailWarningMsg: externalRecipientSet.size > 0,
        disableSubmit: disableSubmit(),
        onClose: onClose,
        onSubmit: handleSubmit,
        assetSpecificTrackingProps: assetSpecificTrackingProps,
        previewUrl: previewUrl,
        objectType: objectType,
        shouldDisplayPreview: !isUnsupportedDataExportType(dataType)
      })
    })]
  });
};
EmailAssetDialog.propTypes = {
  appUsageTracker: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.number,
  isUtc: PropTypes.bool,
  isEditingScheduledEmail: PropTypes.bool,
  ownerId: PropTypes.number,
  nickname: PropTypes.string,
  scheduleFrequency: PropTypes.oneOf(Object.values(SCHEDULE_FREQUENCY)),
  scheduledOnDays: PropTypes.array,
  scheduledAtHour: PropTypes.number,
  subject: PropTypes.string,
  recipientUserIds: PropTypes.array,
  message: PropTypes.string,
  objectType: PropTypes.string.isRequired,
  assetName: PropTypes.string,
  assetSpecificPayload: PropTypes.object,
  assetSpecificTrackingProps: PropTypes.object,
  previewUrl: PropTypes.string.isRequired,
  renderAdditionalFields: PropTypes.func,
  validationError: PropTypes.bool,
  resetSelectedReports: PropTypes.func
};
export default EmailAssetDialog;